import * as Popover from "@radix-ui/react-popover";
import { useMemo, useState } from "react";
import { Check, Search, Tag as TagIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { useTags } from "@/features/data";

interface TagPickerProps {
  value: string[];
  onChange: (ids: string[]) => void;
  compact?: boolean;
}

/** TagPicker is a multi-select over workspace tags; hidden while none exist. */
export function TagPicker({ value, onChange, compact }: TagPickerProps) {
  const { data: tags = [] } = useTags();
  const [q, setQ] = useState("");

  const filtered = useMemo(() => tags.filter((t) => t.name.toLowerCase().includes(q.toLowerCase())), [tags, q]);
  const selected = tags.filter((t) => value.includes(t.id));
  const label = selected.length === 0 ? "Tags" : selected.length === 1 ? selected[0].name : `${selected.length} tags`;

  if (tags.length === 0) return null;

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-2 rounded-md border border-line px-2.5 text-sm transition-colors hover:border-line-strong",
            compact ? "h-8" : "h-9",
            selected.length ? "text-ink" : "text-faint",
          )}
        >
          <TagIcon className="size-4" />
          <span className="max-w-32 truncate">{label}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={6} className="z-50 w-64 overflow-hidden rounded-lg border border-line bg-elevated shadow-xl">
          <div className="flex items-center gap-2 border-b border-line px-3">
            <Search className="size-4 text-faint" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tags…"
              className="h-9 w-full bg-transparent text-sm text-ink placeholder:text-faint focus:outline-none"
            />
          </div>
          <div className="max-h-64 overflow-auto py-1">
            {filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => toggle(t.id)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-ink hover:bg-surface"
              >
                <span className="truncate">{t.name}</span>
                {value.includes(t.id) && <Check className="ml-auto size-4 text-accent" />}
              </button>
            ))}
            {filtered.length === 0 && <p className="px-3 py-4 text-center text-sm text-faint">No tags found</p>}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
